import { defineStore } from "pinia";
import { ref } from "vue";
import errorHandler from "@/services/errorHandler";
import i18n from "@/i18n";
import { validarCondicionesIniciales, enviarARevisarPedido } from "@/helpers/validacionPedido";
import {
  extractEye,
  mapGraduacionToBAPI,
  mapSharedFieldsToBAPI,
  mapAdicionalesToBAPI,
  mapSuplementosToBAPI,
  mapCabeceraToBAPI,
  applyLensesTypeToPedidoCabfrm,
  syncAlturaAndDNP,
  mountEyesIntoPedido,
} from "@/helpers/pedidoBuilder";

// Normaliza un código de organización (VKORG) a 4 dígitos con ceros a la
// izquierda (p.ej. "803" → "0803"). null/"" → null.
const normalizeOrg = (org) => (org ? `${org}`.padStart(4, "0") : null);

export const usePedidosStore = defineStore("pedidos", () => {
  // ── State ──────────────────────────────────────────────────────────────────
  const od = ref(null);
  const oi = ref(null);
  const caracteristicasLente = ref(null);
  const coloracion = ref(null);
  const focomaterial = ref(null);
  const color = ref(null);
  const tono = ref(null);
  const lente = ref(null);
  // VKORG (organización) con el que se cargó la jerga actualmente seleccionada.
  // Es la fuente autoritativa del org del pedido: la cabecera SIEMPRE debe
  // coincidir con él (una jerga solo se puede pedir en su propia organización).
  const lenteVkorg = ref(null);
  const adicionales = ref(null);
  const referencia = ref("");
  const codigoPromocional = ref("");
  const areaCliente = ref({});
  const basicData = ref(null);
  const ImagenesPedido = ref({});
  const fotocromatico = ref(null);
  const tratamiento = ref(null);
  const bisel = ref(false);
  const montaje = ref(false);

  // ── Mutations (direct setters) ─────────────────────────────────────────────
  function setAreaCliente(data) {
    areaCliente.value = data;
  }
  function setBasicData(payload) {
    basicData.value = payload;
  }
  function setOd(data) {
    od.value = data;
  }
  function setOi(data) {
    oi.value = data;
  }
  function setCaracteristicasLente(data) {
    caracteristicasLente.value = data;
  }
  function setColoracion(data) {
    coloracion.value = data;
  }
  function setFocomaterial(data) {
    focomaterial.value = data;
  }
  function setColor(data) {
    color.value = data;
  }
  function setTono(data) {
    tono.value = data;
  }
  function setLente(data) {
    lente.value = data;
  }
  function setLenteVkorg(data) {
    lenteVkorg.value = normalizeOrg(data);
  }
  function setAdicionales(data) {
    adicionales.value = data;
  }
  function setReferencia(data) {
    referencia.value = data;
  }
  function setCodigoPromocional(data) {
    codigoPromocional.value = data;
  }
  function setTratamientoPedido(data) {
    tratamiento.value = data;
  }
  function setFotocromatico(data) {
    fotocromatico.value = data;
  }
  function setImagenesPedido({ codigoPedido, imagen }) {
    ImagenesPedido.value = { ...ImagenesPedido.value, [codigoPedido]: imagen };
  }
  function setBisel(value) {
    bisel.value = value;
  }
  function setMontaje(value) {
    montaje.value = value;
  }
  function guardarImagenPedido({ codigoPedido, imagen }) {
    setImagenesPedido({ codigoPedido, imagen });
  }

  // ── Actions ────────────────────────────────────────────────────────────────

  /**
   * Guarda de coherencia del VKORG: garantiza que la cabecera del pedido lleve
   * SIEMPRE el org bajo el que se cargó la jerga seleccionada. Si divergen (p.ej.
   * el `preferredOrgVenta` global cambió desde otra pantalla mientras la lente
   * seguía siendo de otra marca), se fuerza el org de la jerga —el único con el
   * que SAP puede fabricar esa lente— en cabecera + `listCABIN` y se refresca la
   * descripción. Se reporta como aviso para que la incidencia sea rastreable.
   */
  function enforceVkorgConsistency(pedido, indoStore) {
    const jergaOrg = lenteVkorg.value;
    // Sin jerga seleccionada o sin org capturado no hay nada que verificar.
    if (!lente.value || !jergaOrg) return;

    const headerOrg = normalizeOrg(pedido.VKORG);
    if (headerOrg === jergaOrg) return;

    const areas = indoStore.profile?.AreaVenta?.listaAreasCliente || [];
    const area = areas.find((a) => normalizeOrg(a.ORG_VENTA) === jergaOrg);

    pedido.VKORG = jergaOrg;
    const cabin = pedido.listCABIN?.clsZES_CAB_INPUT?.[0];
    if (cabin) {
      cabin.VKORG = jergaOrg;
      if (area?.TEXTO_ORGANIZACION) cabin.VKORG_DESCR = area.TEXTO_ORGANIZACION.toUpperCase();
    }

    errorHandler.notify({
      type: "warning",
      title: i18n.global.t("organizacion_corregida"),
      message: i18n.global.t("organizacion_corregida_detalle", { pedido: headerOrg, lente: jergaOrg }),
      duration: 6000,
    });
  }

  /**
   * Prepara el pedido para enviar a la API.
   */
  async function preparePedido() {
    const { useIndoStore } = await import("./indo");
    const { useOfertasStore } = await import("./ofertas");
    const { useOthersStore } = await import("./others");
    const indoStore = useIndoStore();
    const ofertasStore = useOfertasStore();
    const othersStore = useOthersStore();
    const pedidoState = indoStore.pedido;
    const pedido = { ...pedidoState.oClaseBAPI };

    applyLensesTypeToPedidoCabfrm(pedido, othersStore.lensesType);

    let ojoInDch = extractEye(pedido, "D");
    let ojoInIzq = extractEye(pedido, "I");

    mapGraduacionToBAPI(od.value, ojoInDch);
    mapGraduacionToBAPI(oi.value, ojoInIzq);

    // Pass a rootState-like object for helpers that need it
    const state = {
      od: od.value,
      oi: oi.value,
      coloracion: coloracion.value,
      fotocromatico: fotocromatico.value,
      focomaterial: focomaterial.value,
      lente: lente.value,
      tratamiento: tratamiento.value,
      tono: tono.value,
      color: color.value,
      adicionales: adicionales.value,
      caracteristicasLente: caracteristicasLente.value,
      referencia: referencia.value,
      codigoPromocional: codigoPromocional.value,
      bisel: bisel.value,
      montaje: montaje.value,
    };
    // Fuente única del org del pedido: el área propia del pedido (la que cargó
    // la jerga) tiene PRIORIDAD sobre el `preferredOrgVenta` global — este último
    // es persistido y puede cambiarse desde otras pantallas, provocando que la
    // cabecera se desincronice de la jerga (VKORG Indo + lente Rodenstock).
    const orderArea = areaCliente.value?.ORG_VENTA ? areaCliente.value : indoStore.preferredOrgVenta;
    const rootState = {
      indo: { profile: indoStore.profile, pedido: indoStore.pedido },
      gama: { areaCliente: orderArea },
      ofertas: { indice: ofertasStore.indice },
    };

    mapSharedFieldsToBAPI(state, rootState, ojoInDch, ojoInIzq);
    mapAdicionalesToBAPI(state, ojoInDch, ojoInIzq, pedido);
    mapSuplementosToBAPI(state, ojoInDch, ojoInIzq);
    syncAlturaAndDNP(pedido, ojoInDch, ojoInIzq);
    mountEyesIntoPedido(pedido, ojoInDch, ojoInIzq);
    mapCabeceraToBAPI(pedido, state, rootState);

    // Guarda de coherencia: la cabecera SIEMPRE debe llevar el org de la jerga
    // seleccionada. Si por cualquier ruta divergen, se fuerza el VKORG de la
    // jerga (org físicamente válido para pedirla) y se reporta la incidencia.
    enforceVkorgConsistency(pedido, indoStore);

    await indoStore.GuardarPedido({ pedido });

    return pedido;
  }

  async function guardarRecetaConDatos() {
    const pedido = await preparePedido();
    const { useIndoStore } = await import("./indo");
    const indoStore = useIndoStore();
    const result = await indoStore.GuardarReceta({ recetaData: pedido });
    return result;
  }

  async function calcularPedido({ pedido, areaCliente: area, review, caracteristicasLente: caracteristicas }) {
    const { useIndoStore } = await import("./indo");
    const indoStore = useIndoStore();
    const isValid = await validarCondicionesIniciales(
      indoStore.profile,
      pedido,
      area,
      review,
      caracteristicas,
    );
    pedido.referencia = pedido.REF_PEDIDO;
    if (Array.isArray(isValid) && isValid.length > 0) throw new Error(JSON.stringify(isValid));

    try {
      const newOrder = await indoStore.PrepararPedido({ pedido });
      const errores = indoStore.GetErroresPedido();
      if (errores.length > 0) throw new Error(JSON.stringify(errores));
      return newOrder;
    } catch (error) {
      if (error.message.startsWith("[")) throw error;
      const errores = indoStore.GetErroresPedido() || [];
      if (errores.length > 0) throw new Error(JSON.stringify(errores));
      throw error;
    }
  }

  async function enviarPedido({ data, areaCliente: area }) {
    const { useIndoStore } = await import("./indo");
    const indoStore = useIndoStore();
    const pedido = indoStore.pedido.oClaseBAPI;
    const prof = indoStore.profile;
    pedido.referencia = pedido.REF_PEDIDO;

    if (!data.review) pedido.MODO_FUNCIONAM = data.modo;
    if (data.KUNWE) pedido.listCABIN.clsZES_CAB_INPUT[0].KUNWE = data.KUNWE;
    if (data.duplicado) pedido.listCABIN.clsZES_CAB_INPUT[0].ACEPTA_DUPLI = "X";

    if (data.review && data.modo !== "A") {
      const isValid = enviarARevisarPedido(pedido, data.od, data.oi, data.lente);
      if (Array.isArray(isValid) && isValid.length > 0) throw new Error(JSON.stringify(isValid));
      pedido.MODO_FUNCIONAM = "B";
    } else if (data.review && data.modo === "A") {
      pedido.MODO_FUNCIONAM = "A";
    }

    const isValid = await validarCondicionesIniciales(
      prof,
      pedido,
      area,
      data.review,
      data.caracteristicasLente,
      undefined,
      true,
    );
    if (Array.isArray(isValid) && isValid.length > 0) throw new Error(JSON.stringify(isValid));

    if (pedido.listCABIN?.clsZES_CAB_INPUT?.[0]) {
      pedido.listCABIN.clsZES_CAB_INPUT[0].AUGRU = "INET";
      pedido.listCABIN.clsZES_CAB_INPUT[0].FLAG_ONES = "X";
    }

    try {
      await indoStore.GuardarPedido({ pedido });
      const newOrder = await indoStore.RealizarPedido({ pedido });
      return newOrder;
    } catch (error) {
      if (error.message.startsWith("[")) throw error;
      const errores = indoStore.GetErroresPedido() || [];
      if (errores.length > 0) throw new Error(JSON.stringify(errores));
      throw error;
    }
  }

  function cambiadoForma({ forma = "0", montaje: mont = null }) {}

  function obtenerImagenPorCodigo(codigoPedido) {
    return ImagenesPedido.value[codigoPedido];
  }

  return {
    // State
    od,
    oi,
    caracteristicasLente,
    coloracion,
    focomaterial,
    color,
    tono,
    lente,
    lenteVkorg,
    adicionales,
    referencia,
    codigoPromocional,
    areaCliente,
    setAreaCliente,
    basicData,
    ImagenesPedido,
    fotocromatico,
    tratamiento,
    bisel,
    montaje,
    // Setters
    setBasicData,
    setOd,
    setOi,
    setCaracteristicasLente,
    setColoracion,
    setFocomaterial,
    setColor,
    setTono,
    setLente,
    setLenteVkorg,
    setAdicionales,
    setReferencia,
    setCodigoPromocional,
    setTratamientoPedido,
    setFotocromatico,
    setImagenesPedido,
    setBisel,
    setMontaje,
    guardarImagenPedido,
    obtenerImagenPorCodigo,
    // Actions
    preparePedido,
    guardarRecetaConDatos,
    calcularPedido,
    enviarPedido,
    cambiadoForma,
  };
});
